import { Cross2Icon, EyeNoneIcon } from '@radix-ui/react-icons';
import { Card, Flex, Grid, Skeleton, Text } from '@radix-ui/themes';
import { CommonDialog } from 'components/common/dialogs';
import { ErrorBoundary } from 'components/common/error-boundary';
import { CommonSearchInput } from 'components/common/form/search';
import { CommonSelectInput } from 'components/common/form/select';
import { PitcherHandBadge } from 'components/common/pitcher-hand-badge';
import usePitchPerformanceStore from 'components/sections/admin-portal/machines/dialogs/pitch-performance/store/use-pitch-performance-store';
import { GameDataPlayerAvatar } from 'components/sections/game-data/players/avatar';
import { GameDataTeamLogo } from 'components/sections/game-data/team-logo';
import { t } from 'i18next';
import { IOptionExt } from 'interfaces/forms/option-ext';
import { StringHelper } from 'lib_ts/classes/string.helper';
import { RADIX } from 'lib_ts/enums/radix-ui';
import { useEffect, useMemo } from 'react';

export const StatcastSearchDialog = () => {
  const statsLoading = usePitchPerformanceStore((s) => s.statsLoading);
  const statsDialog = usePitchPerformanceStore((s) => s.statsDialog);
  const firstGroups = usePitchPerformanceStore((s) => s.firstGroups);
  const statsSeason = usePitchPerformanceStore((s) => s.statsSeason);
  const statsPitchers = usePitchPerformanceStore((s) => s.statsPitchers);
  const statsPitcherPk = usePitchPerformanceStore((s) => s.statsPitcherPk);
  const statsSeasonOptions = usePitchPerformanceStore(
    (s) => s.statsSeasonOptions
  );
  const statsPitchGroups = usePitchPerformanceStore((s) => s.statsPitchGroups);

  useEffect(() => {
    if (statsSeasonOptions.length === 0) {
      // load seasons 1x
      usePitchPerformanceStore.getState().fetchStatsSeasons();
      return;
    }

    if (statsSeason) {
      // don't manipulate season if already set from before
      return;
    }
    // select the first season (should be the latest one) by default
    usePitchPerformanceStore
      .getState()
      .setStatsSeason(parseInt(statsSeasonOptions[0].value));
  }, [statsSeasonOptions]);

  useEffect(() => {
    if (!statsSeason) {
      return;
    }

    usePitchPerformanceStore.getState().fetchStatsPitchers();
  }, [statsSeason]);

  useEffect(() => {
    usePitchPerformanceStore.getState().fetchStatsPitches(firstGroups, false);
  }, [statsPitcherPk]);

  const pitcherOptions = useMemo(() => {
    const NO_TEAM_GROUP = 'No Active Roster';

    const options: IOptionExt[] = statsPitchers
      .filter((p) => p._mx_details?.isPitcher)
      .map((p) => {
        const o: IOptionExt = {
          label: p.name,
          value: p.playerPk.toString(),
          group: p._mx_team_details?.shortName ?? NO_TEAM_GROUP,
          suffix: <PitcherHandBadge value={p.pitchHand} />,
        };

        return o;
      })
      // todo: not really sure why but this causes the cardinals group to sort to the top
      .sort((a, b) => {
        if (a.group === b.group) {
          // within a team, sort by player name alphabetically
          return StringHelper.safeSort(a.label, b.label);
        }

        // put no roster stuff at the end
        if (a.group === NO_TEAM_GROUP) {
          return 1;
        }

        // put no roster stuff at the end
        if (b.group === NO_TEAM_GROUP) {
          return -1;
        }

        // across teams, sort by team names alphabetically
        return StringHelper.safeSort(a.group, b.group);
      });

    return options;
  }, [statsPitchers]);

  const pitcher = useMemo(() => {
    if (!statsPitcherPk) {
      return undefined;
    }

    return statsPitchers.find((p) => p.playerPk === statsPitcherPk);
  }, [statsPitchers, statsPitcherPk]);

  return (
    <ErrorBoundary identifier="StatcastSearchDialog">
      <CommonDialog
        key={statsDialog}
        identifier="statcast-search-dialog"
        width={RADIX.DIALOG.WIDTH.SM}
        title="Statcast Search"
        content={
          <Flex direction="column" gap="2">
            <CommonSelectInput
              id="statcast-season"
              placeholder="Select Season"
              disabled={statsLoading}
              value={statsSeason?.toString()}
              options={statsSeasonOptions}
              onNumericChange={(v) =>
                usePitchPerformanceStore.getState().setStatsSeason(v)
              }
              // preserve newest season first
              skipSort
              optional
            />

            <CommonSearchInput
              id="statcast-pitcherPk"
              placeholder="Select Pitcher"
              disabled={statsLoading}
              values={statsPitcherPk ? [statsPitcherPk.toString()] : []}
              options={pitcherOptions}
              onChange={(v) => {
                if (v.length === 0) {
                  usePitchPerformanceStore
                    .getState()
                    .setStatsPitcherPk(undefined);
                  return;
                }

                usePitchPerformanceStore
                  .getState()
                  .setStatsPitcherPk(parseInt(v[0]));
              }}
              skipSort
              optional
            />

            {statsLoading && <Skeleton height="100px" />}

            {!statsLoading && pitcher && (
              <Card size="1" className="bg-background">
                <Flex direction="column" gap="3">
                  {/* pitcher summary */}
                  <Flex gap="2" style={{ width: '100%' }} align="center">
                    <GameDataPlayerAvatar
                      size="2"
                      playerPk={pitcher.playerPk}
                      name={pitcher.name}
                    />
                    <Flex direction="column" flexGrow="1">
                      <Text>{pitcher.name}</Text>

                      <Flex align="center" gap="1">
                        <GameDataTeamLogo
                          teamPK={pitcher._mx_team_details?.teamPk}
                          size="16"
                        />
                        <Text size={RADIX.TEXT.SIZE.HINT}>
                          {pitcher._mx_team_details?.shortName}
                        </Text>
                      </Flex>
                    </Flex>

                    <PitcherHandBadge value={pitcher.pitchHand} />
                  </Flex>

                  <Grid columns="2" gap="1">
                    {statsPitchGroups.map((p, i) => (
                      <Flex gap="1" key={i} align="center">
                        {p.invisible ? (
                          <EyeNoneIcon color={RADIX.COLOR.NEUTRAL} />
                        ) : (
                          <Cross2Icon style={{ color: `rgb(${p.rgb})` }} />
                        )}
                        {t(p.typeName)} ({p.type})
                      </Flex>
                    ))}
                  </Grid>
                </Flex>
              </Card>
            )}
          </Flex>
        }
        buttons={[
          {
            label: 'Clear Pitcher',
            disabled: !statsPitcherPk || statsLoading || !pitcher,
            onClick: () =>
              usePitchPerformanceStore.getState().setStatsPitcherPk(undefined),
          },
          {
            label: 'Done',
            variant: RADIX.BUTTON.VARIANT.PRIMARY,
            onClick: () =>
              usePitchPerformanceStore.getState().setStatsDialog(undefined),
          },
        ]}
        onClose={() =>
          usePitchPerformanceStore.getState().setStatsDialog(undefined)
        }
      />
    </ErrorBoundary>
  );
};
